import type { HasNoRequiredProperties, Simplify } from '@epihub/core/src/ts';
import { SfyrSyncDatabase } from './database';
import { SfyrContext } from './context';
import type { SfyrAnyModel, SfyrAnySchema } from './types';
import { extractBoundVarsFromKey, generateSpecificityVariations } from './utils';

/**
 * Check if a type is Record<string, never> (empty record)
 */
type IsEmptyRecord<T> = T extends Record<string, never> ? keyof T extends never ? false // Has index signature but no specific keys
  : true
  : false;

/**
 * Merge two var types, handling Record<string, never> properly.
 * Record<string, never> should be treated as "no vars" and not pollute the intersection.
 */
type MergeVars<R, V> =
  // If R has no specific keys (is Record<string, never>), just use V
  keyof R extends never ? keyof V extends never ? {} // Both empty
    : V
    : keyof V extends never ? R // V is empty, just use R
    : Simplify<R & V>; // Both have keys, merge them

/**
 * Combines resolverVars and valueVars from a Model entry
 */
type CombinedVars<Entry> = Entry extends { resolverVars: infer R; valueVars: infer V } ? MergeVars<R, V>
  : {};

/**
 * Extract bound variable names from a resolver key.
 * e.g., 'ui.{lang}.events.{event_slug=foo}' -> 'event_slug'
 * Variables with '=' are bound and don't need to be provided in vars.
 */
type ExtractBoundVars<K extends string> = K extends `${string}{${infer Var}=${string}}${infer Rest}`
  ? Var | ExtractBoundVars<Rest>
  : K extends `${string}{${infer _Var}}${infer Rest}` ? ExtractBoundVars<Rest>
  : never;

/**
 * Extract ALL variable names from a resolver key (both bound and unbound).
 * e.g., 'ui.{lang}.events.{event_slug=foo}' -> 'lang' | 'event_slug'
 * Uses recursive extraction to find all {var} and {var=value} patterns.
 */
type ExtractAllVars<K extends string> = K extends `${infer Before}{${infer Var}=${infer _Value}}${infer After}`
  ? Var | ExtractAllVars<`${Before}${After}`>
  : K extends `${infer Before}{${infer Var}}${infer After}` ? Var | ExtractAllVars<`${Before}${After}`>
  : never;

/**
 * Create a resolver vars type from just the key, without looking up Model[K].
 * All vars are typed as string since we derive them from the key pattern.
 */
type ResolverVarsFromKey<K extends string> = {
  [V in ExtractAllVars<K>]: string;
};

/**
 * Makes keys optional if they exist in Provided (Context or are bound in the resolver key).
 */
type VarsAfterProvided<Vars, Provided> = Simplify<
  & {
    // Required: keys not in Provided
    [K in keyof Vars as K extends Provided ? never : K]: Vars[K];
  }
  & {
    // Optional: keys that exist in Provided
    [K in keyof Vars as K extends Provided ? K : never]?: Vars[K];
  }
>;

/**
 * The vars type after applying Context and bound variables from the resolver key.
 * - Keys in Context become optional
 * - Keys bound in the resolver (like {var=value}) become optional
 */
type ResolvedVars<Entry, Context, ResolverKey extends string> = VarsAfterProvided<
  CombinedVars<Entry>,
  keyof Context | ExtractBoundVars<ResolverKey>
>;

/**
 * Extract the 'type' field from a Model entry
 */
type EntryType<Entry> = Entry extends { type: infer T } ? T : never;

/**
 * Check if a resolver key contains a wildcard pattern ('.*')
 */
type IsWildcardResolver<K extends string> = K extends `${string}.*${string}` ? true : false;

/**
 * Extract the base value type for defaults.
 * - For wildcard resolvers with `Array<[string, V]>` (entries tuple), returns `V`
 * - For wildcard resolvers with `Array<T>` (regular array), returns `T`
 * - For non-wildcard resolvers, returns the full type as-is
 */
type DefaultsValueType<Entry, ResolverKey extends string> = Entry extends { type: infer T }
  ? IsWildcardResolver<ResolverKey> extends true ? T extends Array<[string, infer V]> ? V // Wildcard entries tuple: extract value type
    : T extends Array<infer E> ? E // Wildcard regular array: extract element
    : T // Wildcard non-array: use as-is
  : T // Non-wildcard: always use full type
  : never;

/**
 * Options when vars are required (has at least one required key)
 */
type OptsWithRequiredVars<Entry, Context, ResolverKey extends string> = {
  vars: ResolvedVars<Entry, Context, ResolverKey>;
  /**
   * Fallback values for resolver key variations.
   * Keys are path variations (e.g., 'ui.{lang=en}.title', 'ui.{lang}.title'),
   * values are the pre-resolved values for those paths.
   * For wildcard resolvers (returning Array<T>), values are of type T.
   * For non-wildcard resolvers, values are the full type.
   */
  defaults?: Record<string, DefaultsValueType<Entry, ResolverKey>>;
};

/**
 * Options when vars are optional (all keys covered by Context + bound vars)
 */
type OptsWithOptionalVars<Entry, Context, ResolverKey extends string> = {
  vars?: ResolvedVars<Entry, Context, ResolverKey>;
  /**
   * Fallback values for resolver key variations.
   * Keys are path variations (e.g., 'ui.{lang=en}.title', 'ui.{lang}.title'),
   * values are the pre-resolved values for those paths.
   * For wildcard resolvers (returning Array<T>), values are of type T.
   * For non-wildcard resolvers, values are the full type.
   */
  defaults?: Record<string, DefaultsValueType<Entry, ResolverKey>>;
};

/**
 * The resolver function signature - used for both direct calls and .r() method
 */
export type SfyrResolver<Model extends SfyrAnyModel, Context extends Record<string, any>> = <
  K extends keyof Model & string,
>(
  resolver: K,
  ...opts:
    HasNoRequiredProperties<VarsAfterProvided<ResolverVarsFromKey<K>, keyof Context | ExtractBoundVars<K>>> extends true
      ? [opts?: OptsWithOptionalVars<Model[K], Context, K>]
      : [opts: OptsWithRequiredVars<Model[K], Context, K>]
) => Model[K]['type'];

/**
 * The Sfyr instance type - a callable function with additional properties.
 * Can be used as:
 * - sfyr('ui.{lang}.title') - direct call
 * - sfyr.r('ui.{lang}.title') - method call (alias)
 */
export type Sfyr<
  Model extends SfyrAnyModel = SfyrAnyModel,
  Context extends Record<string, any> = Record<string, any>,
> = SfyrResolver<Model, Context> & {
  /** Alias for calling sfyr directly */
  r: SfyrResolver<Model, Context>;
  /** The schema definition */
  schema: SfyrAnySchema;
  /** The context manager */
  context: SfyrContext<Context>;
  /** The database instance */
  db: SfyrSyncDatabase;
  /** Type helper for accessing Model and Context types */
  _: {
    Model: Model;
    Context: Context;
  };
};

/**
 * Create a Sfyr instance.
 * Returns a callable function that also has properties like .r, .context, .db
 */
export function createSfyr<
  Model extends SfyrAnyModel = SfyrAnyModel,
  Context extends Record<string, any> = Record<string, any>,
>(schema: SfyrAnySchema, initialContext: Context): Sfyr<Model, Context> {
  const context = new SfyrContext(initialContext);
  const db = new SfyrSyncDatabase(schema);

  /**
   * Resolve a value from the model by key.
   *
   * The `vars` parameter combines `resolverVars` (from the path) and `valueVars` (from the schema).
   * - Keys that exist in Context become optional (pulled from context)
   * - Keys bound in the resolver key (like `{var=value}`) become optional (value is in the key)
   * - If all required keys are covered, the entire `opts` parameter becomes optional
   *
   * @example
   * // Direct call syntax
   * sfyr('ui.{lang}.title', { vars: { lang: 'en' } })
   *
   * @example
   * // Method call syntax (alias)
   * sfyr.r('ui.{lang}.title', { vars: { lang: 'en' } })
   *
   * @example
   * // With context providing 'lang', vars becomes optional
   * sfyr('ui.{lang}.title') // lang pulled from context
   *
   * @example
   * // With bound variable in key, that var is not required
   * sfyr('ui.{lang}.events.{event_slug=featured}') // event_slug bound to 'featured'
   *
   * @example
   * // With defaults as fallback values for path variations
   * sfyr('ui.{lang}.calendar.events.{event_slug}', {
   *   vars: { event_slug: 'featured' },
   *   defaults: {
   *     'ui.{lang}.calendar.events.{event_slug=featured}': [{ title: 'Featured', description: '...' }],
   *     'ui.{lang=en}.calendar.events.{event_slug=featured}': [{ title: 'Featured EN', description: '...' }],
   *   }
   * })
   */
  const resolve = (resolver: string, ...optsArray: any[]) => {
    const opts = optsArray[0] as { vars?: Record<string, string>; defaults?: Record<string, any> } | undefined;

    // 1. Build the resolution vars with priority: key-bound > opts.vars > context
    const keyBoundVars = extractBoundVarsFromKey(resolver);
    const optsVars = opts?.vars ?? {};
    const contextVars = context.getAll() as Record<string, string>;

    const resolvedVars: Record<string, string> = {
      ...contextVars,
      ...optsVars,
      ...keyBoundVars, // Key-bound vars have highest priority
    };

    // 2. Hydrate database with defaults (never replaces existing values)
    if (opts?.defaults) {
      hydrateDefaults(db, opts.defaults);
    }

    // 3. Check for wildcard patterns
    if (resolver.includes('.*')) {
      return resolveWildcard(resolver, resolvedVars, db);
    }

    // 4. Search database in specificity order (most specific to least specific)
    const variations = generateSpecificityVariations(resolver, resolvedVars);

    for (const key of variations) {
      const value = db.getLocal(key);
      if (value !== undefined) {
        return value;
      }
    }

    // 5. No value found - throw error
    const fullyResolvedKey = variations[0]; // Most specific variation
    throw new Error(`Sfyr: No value found for resolver '${resolver}' (resolved to '${fullyResolvedKey}')`);
  };

  /**
   * Resolve wildcard patterns like 'path.*.field' or 'path.*'.
   * Returns an array of values matching the pattern.
   */
  function resolveWildcard(
    resolver: string,
    vars: Record<string, string>,
    _db: SfyrSyncDatabase,
  ): any[] {
    // Split resolver into parts around the wildcard
    const parts = resolver.split('.*');
    const prefix = parts[0]; // e.g., 'ui.{lang}.calendar.events'
    const suffix = parts.slice(1).join('.*'); // e.g., '.eventSlug' or '' or '.*.nested'

    // Resolve prefix variables
    const resolvedPrefix = resolvePathWithVars(prefix, vars);

    // Find all keys matching the prefix pattern
    const results: any[] = [];
    const allKeys = db.getAllKeys();

    for (const key of allKeys) {
      // Check if key starts with resolved prefix and has a variable segment after
      if (keyMatchesWildcardPrefix(key, resolvedPrefix)) {
        // Extract the part after the prefix
        const remainder = key.slice(resolvedPrefix.length);

        // The next segment should be a bound variable like {slug=value}
        if (remainder.startsWith('.{') && remainder.includes('=')) {
          const nextDotIndex = remainder.indexOf('.', 1);
          const varSegment = nextDotIndex === -1
            ? remainder.slice(1)
            : remainder.slice(1, nextDotIndex);

          // Extract the bound value from varSegment (e.g., "short_chat" from "{slug=short_chat}")
          const boundValue = varSegment.slice(varSegment.indexOf('=') + 1, -1);

          // If there's a suffix, we need to check if the key continues with it
          if (suffix) {
            const suffixToCheck = suffix.startsWith('.') ? suffix.slice(1) : suffix;
            const keyAfterVar = nextDotIndex === -1 ? '' : remainder.slice(nextDotIndex + 1);

            // Resolve suffix variables and check match
            const resolvedSuffix = resolvePathWithVars(suffixToCheck, vars);
            if (keyAfterVar === resolvedSuffix || keyMatchesPattern(keyAfterVar, resolvedSuffix)) {
              const value = db.getLocal(key);
              if (value !== undefined) {
                results.push([boundValue, value]);
              }
            }
          } else {
            // No suffix, just collect the value at this key
            const value = db.getLocal(key);
            if (value !== undefined) {
              results.push([boundValue, value]);
            }
          }
        }
      }
    }

    return results;
  }

  /**
   * Check if a key matches a wildcard prefix (before the *)
   */
  function keyMatchesWildcardPrefix(key: string, prefix: string): boolean {
    // Handle exact prefix match
    if (key.startsWith(prefix + '.')) {
      return true;
    }

    // Handle prefix with unresolved variables matching bound variables
    const keySegments = key.split('.');
    const prefixSegments = prefix.split('.');

    if (keySegments.length < prefixSegments.length) {
      return false;
    }

    for (let i = 0; i < prefixSegments.length; i++) {
      const keySeg = keySegments[i];
      const prefixSeg = prefixSegments[i];

      // Check if segments match (including variable matching)
      if (!segmentsMatch(keySeg, prefixSeg)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Check if two segments match, handling variables
   */
  function segmentsMatch(keySeg: string, prefixSeg: string): boolean {
    if (keySeg === prefixSeg) {
      return true;
    }

    // Handle variable matching
    if (keySeg.startsWith('{') && prefixSeg.startsWith('{')) {
      const keyVarName = keySeg.includes('=')
        ? keySeg.slice(1, keySeg.indexOf('='))
        : keySeg.slice(1, -1);
      const keyVarValue = keySeg.includes('=')
        ? keySeg.slice(keySeg.indexOf('=') + 1, -1)
        : null;

      const prefixVarName = prefixSeg.includes('=')
        ? prefixSeg.slice(1, prefixSeg.indexOf('='))
        : prefixSeg.slice(1, -1);
      const prefixVarValue = prefixSeg.includes('=')
        ? prefixSeg.slice(prefixSeg.indexOf('=') + 1, -1)
        : null;

      // Variable names must match
      if (keyVarName !== prefixVarName) {
        return false;
      }

      // If prefix has a bound value, key must have the same value
      if (prefixVarValue !== null) {
        return keyVarValue === prefixVarValue;
      }

      // If prefix is unbound, any key value matches
      return true;
    }

    return false;
  }

  /**
   * Check if a key pattern matches a target pattern
   */
  function keyMatchesPattern(key: string, pattern: string): boolean {
    const keySegments = key.split('.');
    const patternSegments = pattern.split('.');

    if (keySegments.length !== patternSegments.length) {
      return false;
    }

    for (let i = 0; i < patternSegments.length; i++) {
      if (!segmentsMatch(keySegments[i], patternSegments[i])) {
        return false;
      }
    }

    return true;
  }

  /**
   * Resolve a path by substituting variables
   */
  function resolvePathWithVars(path: string, vars: Record<string, string>): string {
    return path.replace(/\{([^}=]+)\}/g, (match, varName) => {
      const value = vars[varName];
      return value !== undefined ? `{${varName}=${value}}` : match;
    });
  }

  /**
   * Hydrate the database with default values.
   * Never replaces existing values - only adds if the exact key doesn't exist.
   */
  function hydrateDefaults(_db: SfyrSyncDatabase, defaults: Record<string, any>): void {
    for (const [defaultKey, defaultValue] of Object.entries(defaults)) {
      // Only add if this exact key doesn't already exist in the database
      if (!db.hasLocal(defaultKey)) {
        db.setLocal(defaultKey, defaultValue);
      }
    }
  }

  // Create the callable with properties
  const sfyr = resolve as Sfyr<Model, Context>;
  sfyr.r = resolve as SfyrResolver<Model, Context>;
  sfyr.schema = schema;
  sfyr.context = context;
  sfyr.db = db;

  return sfyr;
}
